import React from "react";
import { CssBaseline, Box, Drawer, IconButton, AppBar, Toolbar, Typography, Tooltip, Select, MenuItem } from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CodeEditor from "./CodeEditor";
import RichTextEditor from "./RichTextEditor";
import AuthForm from "./AuthForm";
import WorkspaceSelector from "./WorkspaceSelector";
import NoteVersions from "./NoteVersions";
import { TextField, List, ListItem, ListItemButton, ListItemText, Divider, Snackbar, Button } from "@mui/material";

function App() {
  // Store all share links for the selected note
  const [shareLinks, setShareLinks] = React.useState([]);
  const [codeLanguage, setCodeLanguage] = React.useState('javascript');
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [note, setNote] = React.useState("");
  const [format, setFormat] = React.useState("plain");
  const [auth, setAuth] = React.useState(() => {
    const token = localStorage.getItem("token");
    const email = localStorage.getItem("email");
    return token && email ? { token, email } : null;
  });
  const [noteName, setNoteName] = React.useState("");
  const [workspace, setWorkspace] = React.useState(() => localStorage.getItem("workspace") || "");
  const [notesList, setNotesList] = React.useState([]);
  const [noteSearch, setNoteSearch] = React.useState("");
  const filteredNotes = noteSearch
    ? notesList.filter(n => n.name.toLowerCase().includes(noteSearch.toLowerCase()))
    : notesList;
  const [selectedNoteName, setSelectedNoteName] = React.useState(null);
  const [loadingNotes, setLoadingNotes] = React.useState(false);
  const [saveSuccess, setSaveSuccess] = React.useState(false);
  const [versionsOpen, setVersionsOpen] = React.useState(false);

  // Handle login/logout
  const handleAuth = (token, email) => {
    localStorage.setItem("token", token);
    localStorage.setItem("email", email);
    setAuth({ token, email });
    setWorkspace("");
    setNotesList([]);
    setSelectedNoteName(null);
  };
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    setAuth(null);
    setWorkspace("");
    setNotesList([]);
    setSelectedNoteName(null);
  };

  // Remove all localStorage note content logic. Only keep note in memory.


  // Track last saved/loaded note for unsaved changes warning
  const [lastSaved, setLastSaved] = React.useState({ note: "", noteName: "", format: "plain" });

  // When a note is loaded, set editor state and lastSaved
  React.useEffect(() => {
    if (selectedNoteName) {
      fetch(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/notes/${encodeURIComponent(selectedNoteName)}?workspace=${encodeURIComponent(workspace)}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      })
        .then(r => r.json())
        .then(data => {
          setNote(data.content || "");
          setNoteName(data.name || "");
          setFormat(data.format || "plain");
          setLastSaved({ note: data.content || "", noteName: data.name || "", format: data.format || "plain" });
        });
      // Fetch all share links for this note
      fetch(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/shared/raw-links/${encodeURIComponent(selectedNoteName)}`)
        .then(r => r.ok ? r.json() : [])
        .then(setShareLinks)
        .catch(() => setShareLinks([]));
    } else {
      setNote("");
      setNoteName("");
      setFormat("plain");
      setLastSaved({ note: "", noteName: "", format: "plain" });
      setShareLinks([]);
    }
  }, [selectedNoteName, workspace, auth]);

  // When a note is saved
  const handleSaveNote = async () => {
    if (!noteName || !workspace) return;
    await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/notes`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth.token}`
      },
      body: JSON.stringify({
        name: noteName,
        content: note,
        format,
        workspace
      })
    });
    setSaveSuccess(true);
    setLastSaved({ note, noteName, format });
  };


  // Note change handler (in memory only)
  const handleNoteChange = (e) => {
    setNote(e.target.value);
  };

  // For CodeEditor (uses value, not event)
  const handleCodeChange = (value) => {
    setNote(value);
  };

  // For RichTextEditor
  const handleRichChange = (value) => {
    setNote(value);
  };

  // Format switching with unsaved changes warning
  const handleFormatChange = (e) => {
    if (note !== lastSaved.note) {
      const confirmSwitch = window.confirm("You have unsaved changes. Switching note format will discard them. Continue?");
      if (!confirmSwitch) return;
    }
    setFormat(e.target.value);
    setNote("");
  };

  // List notes for workspace
  React.useEffect(() => {
    if (!auth || !workspace) return;
    setLoadingNotes(true);
    fetch(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/notes?workspace=${encodeURIComponent(workspace)}`, {
      headers: { Authorization: `Bearer ${auth.token}` }
    })
      .then(r => r.json())
      .then(data => { setNotesList(data); setLoadingNotes(false); })
      .catch(() => setLoadingNotes(false));
  }, [auth, workspace, saveSuccess]);

  // Load note when selected
  React.useEffect(() => {
    if (!auth || !workspace || !selectedNoteName) return;
    fetch(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/notes/${encodeURIComponent(selectedNoteName)}?workspace=${encodeURIComponent(workspace)}`, {
      headers: { Authorization: `Bearer ${auth.token}` }
    })
      .then(r => r.json())
      .then(data => {
        setNote(data.content || "");
        setNoteName(data.name || "");
        setFormat(data.format || "plain");
      });
  }, [selectedNoteName]);

  // No localStorage for workspace


  // Default to first available workspace after login or workspace list change
  React.useEffect(() => {
    if (!auth) return;
    fetch(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/workspaces`, {
      headers: { Authorization: `Bearer ${auth.token}` }
    })
      .then(r => r.json())
      .then(ws => {
        if ((!workspace || !ws.includes(workspace)) && ws.length > 0) {
          setWorkspace(ws[0]);
        }
      });
  }, [auth]);

  // Warn about unsaved changes if user tries to close tab/browser
  React.useEffect(() => {
    const handler = (e) => {
      if (note !== lastSaved.note) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Save before closing?";
        return "You have unsaved changes. Save before closing?";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [note, lastSaved]);

  if (!auth) {
    return <AuthForm onAuth={handleAuth} />;
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      <CssBaseline />
      <AppBar position="static" color="transparent" elevation={0} sx={{ boxShadow: 'none', mb: 0 }}> 
        <Toolbar sx={{ minHeight: 48, px: 1, py: 0 }}> 
          <IconButton edge="start" color="inherit" aria-label="menu" onClick={() => setDrawerOpen(true)}>
            <MenuIcon />
          </IconButton>
          <TextField
            variant="standard"
            value={noteName}
            onChange={e => setNoteName(e.target.value)}
            placeholder="Note Name"
            InputProps={{
              disableUnderline: true,
              style: {
                fontSize: 20,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                background: 'transparent',
                paddingLeft: 6,
                paddingRight: 6,
                maxWidth: 225
              }
            }}
            sx={{ flexGrow: 1, mx: 2 }}
          />
        </Toolbar>
      </AppBar>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'stretch', justifyContent: 'center', p: 0, m: 0, overflowX: 'hidden' }}>
        <Box sx={{ bgcolor: 'white', borderRadius: 0, width: '100%', maxWidth: '100%', boxShadow: 0, p: 0, flex: 1, display: 'flex', flexDirection: 'column', overflowX: 'hidden', height: '100%', mt: 0 }}>
          {format === "plain" && (
            <textarea
              value={note}
              onChange={handleNoteChange}
              placeholder="Start typing your note here..."
              style={{
                width: '100%',
                maxWidth: '100%',
                height: '100%',
                resize: 'none',
                border: 'none',
                outline: 'none',
                fontSize: '1.1rem',
                padding: '20px',
                background: 'transparent',
                color: '#222',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                flex: 1,
                borderRadius: '16px',
                margin: 0
              }}
            />
          )}
          {format === "code" && (
            <CodeEditor value={note} onChange={handleCodeChange} height="100%" language={codeLanguage} />
          )}
          {format === "rich" && (
            <RichTextEditor value={note} onChange={handleRichChange} />
          )}
        </Box>
      </Box>
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 280, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{auth.email}</Typography>
            <Tooltip title="Logout">
              <IconButton color="secondary" onClick={handleLogout} size="small">
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ mr: 1 }}>Format:</Typography>
            <Select
              size="small"
              value={format}
              onChange={handleFormatChange}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="plain">Plain</MenuItem>
              <MenuItem value="code">Code</MenuItem>
              <MenuItem value="rich">Rich</MenuItem>
            </Select>
          </Box>
          {format === "code" && (
            <Box sx={{ mt: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ mr: 1 }}>Language:</Typography>
              <Select
                size="small"
                value={codeLanguage}
                onChange={e => setCodeLanguage(e.target.value)}
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="javascript">JavaScript</MenuItem>
                <MenuItem value="python">Python</MenuItem>
                <MenuItem value="java">Java</MenuItem>
                <MenuItem value="c">C</MenuItem>
                <MenuItem value="cpp">C++</MenuItem>
                <MenuItem value="go">Go</MenuItem>
                <MenuItem value="ruby">Ruby</MenuItem>
                <MenuItem value="php">PHP</MenuItem>
                <MenuItem value="typescript">TypeScript</MenuItem>
                <MenuItem value="rust">Rust</MenuItem>
                <MenuItem value="markdown">Markdown</MenuItem>
              </Select>
            </Box>
          )}
          <WorkspaceSelector token={auth.token} value={workspace} onChange={setWorkspace} />
          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 2, justifyContent: 'center' }}>
            <Tooltip title="New Note">
              <span>
                <IconButton color="primary" onClick={() => {
                  if (note !== lastSaved.note) {
                    const confirmNew = window.confirm("You have unsaved changes. Starting a new note will discard them. Continue?");
                    if (!confirmNew) return;
                  }
                if (note !== lastSaved.note) {
                  const confirmNew = window.confirm("You have unsaved changes. Starting a new note will discard them. Continue?");
                  if (!confirmNew) return;
                }
                setNote("");
                setNoteName("");
                setFormat("plain");
                setLastSaved({ note: "", noteName: "", format: "plain" });
                setSelectedNoteName(null);
              }}>
                <AddIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Save Note">
            <span>
              <IconButton color="success" onClick={handleSaveNote} disabled={!noteName || !workspace}>
                <SaveIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete Note">
            <span>
              <IconButton color="error" onClick={async () => {
                if (!selectedNoteName || !workspace) return;
                const confirmDelete = window.confirm("Are you sure you want to delete this note? This action cannot be undone.");
                if (!confirmDelete) return;
                const res = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/notes/${encodeURIComponent(selectedNoteName)}?workspace=${encodeURIComponent(workspace)}`,
                  { method: "DELETE", headers: { Authorization: `Bearer ${auth.token}` } }
                );
                if (res.ok) {
                  setNotesList(notesList.filter(n => n.name !== selectedNoteName));
                  setSelectedNoteName(null);
                  setNote("");
                  setNoteName("");
                  setFormat("plain");
                  setLastSaved({ note: "", noteName: "", format: "plain" });
                  setShareLinks([]);
                  alert('Note deleted!');
                } else {
                  alert('Failed to delete note.');
                }
              }} disabled={!selectedNoteName || !workspace}>
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Share Note">
            <span>
              <IconButton color="info" onClick={async () => {
                const res = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/shared/raw`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: auth.email, workspace, name: selectedNoteName })
                });
                if (res.ok) {
                  const data = await res.json();
                  // The backend returns data.url as /shared/raw/<shareId>/<note name>, but if it ever returns an /api/ path, convert it
                  const apiOrigin = process.env.REACT_APP_API_PUBLIC_URL || window.location.origin;
                  let shareUrl = data.url;
                  if (shareUrl.startsWith('/api/shared/raw/')) {
                    shareUrl = shareUrl.replace('/api', '');
                  }
                  window.prompt('Shareable raw note URL:', `${apiOrigin}${shareUrl}`);
                } else {
                  alert('Failed to create share link');
                }
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 12a4 4 0 0 0-3.995 3.8L12 16a4 4 0 0 0 7.995.2L20 16a1 1 0 1 0-2 0 2 2 0 1 0-3.995.15L14 16a2 2 0 1 1 2-2h-2Zm-8-4a4 4 0 0 1 3.995-3.8L12 4a4 4 0 0 1 .2 7.995L12 12a1 1 0 1 1 0-2 2 2 0 1 0-2-2H8Zm8 0a2 2 0 1 1-2 2h2a2 2 0 1 0-2-2 4 4 0 0 1 3.995-3.8L16 4a4 4 0 0 1 .2 7.995L16 12a1 1 0 1 1 0-2 2 2 0 1 0-2-2h2Z" fill="#1976d2"/></svg>
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {/* Versions Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Tooltip title="Show Note Versions">
            <span>
              <IconButton color="primary" onClick={() => setVersionsOpen(true)} disabled={!selectedNoteName}>
                <span role="img" aria-label="versions">🕑</span>
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <NoteVersions
          open={versionsOpen}
          onClose={() => setVersionsOpen(false)}
          noteName={selectedNoteName}
          workspace={workspace}
          token={auth.token}
        />
        {shareLinks.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 2, gap: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Active Share Links</Typography>
            {shareLinks.map(link => (
              <Box key={link.shareId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%' }}>
                <Tooltip title="Copy Link">
                  <IconButton size="small" onClick={() => navigator.clipboard.writeText(`${process.env.REACT_APP_API_PUBLIC_URL || window.location.origin}/shared/raw/${link.shareId}/${encodeURIComponent(link.name)}`)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Revoke Link">
                  <IconButton size="small" color="warning" onClick={async () => {
                    const res = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/shared/raw/${link.shareId}/${encodeURIComponent(link.name)}`,
                      { method: "DELETE" }
                    );
                    if (res.ok) {
                      setShareLinks(shareLinks.filter(l => l.shareId !== link.shareId));
                      alert('Share link revoked!');
                    } else {
                      alert('Failed to revoke share link. It may already be deleted.');
                    }
                  }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <span style={{ fontSize: 12, opacity: 0.7 }}>{link.shareId}</span>
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ width: '100%', p: 2 }}>
          <TextField
            label="Search notes"
            value={noteSearch || ''}
            onChange={e => setNoteSearch(e.target.value)}
            sx={{ width: '100%', mb: 1 }}
          />
          <List sx={{
            width: '100%',
            maxWidth: 360,
            bgcolor: 'background.paper',
            position: 'relative',
            overflow: 'auto',
            maxHeight: {
              xs: 'calc(100vh - 240px)',
              sm: 'calc(100vh - 260px)',
              md: 'calc(100vh - 280px)',
              lg: 'calc(100vh - 300px)'
            },
            '& ul': { padding: 0 },
            '& .MuiListItemButton-root': {
              pl: 4,
              pr: 4,
            },
          }}
          >
            {loadingNotes ? (
              <ListItem><ListItemText primary="Loading..." /></ListItem>
            ) : filteredNotes.length === 0 ? (
              <ListItem><ListItemText primary="No notes" /></ListItem>
            ) : [...filteredNotes].sort((a, b) => {
                const tA = b.lastModified ? new Date(b.lastModified).getTime() : 0;
                const tB = a.lastModified ? new Date(a.lastModified).getTime() : 0;
                return tA - tB;
              }).map(note => (
                <ListItemButton key={note.name} selected={note.name === selectedNoteName} onClick={() => setSelectedNoteName(note.name)}>
                  <ListItemText primary={note.name} secondary={note.lastModified ? new Date(note.lastModified).toLocaleString() : undefined} />
                </ListItemButton>
              ))}
          </List>
        </Box>
      </Box>
    </Drawer>
    <Snackbar
      open={saveSuccess}
      autoHideDuration={2000}
      onClose={() => setSaveSuccess(false)}
      message="Note saved!"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />
  </Box>
);
}

export default App;
